import * as React from "react";
import { applyMode as applyCloudscapeMode, Mode as CloudscapeMode } from "@cloudscape-design/global-styles";
import { applyMode as applyAwsuiMode, Mode as AwsuiMode } from "@amzn/awsui-global-styles";
import { applyTheme } from "@cloudscape-design/components/theming";
import { useDebug } from "@amzn/awsmp-max-agentic-renderer";
import { primaryButtonTheme } from "./primary-button-theme";

export interface ThemeProviderProps {
  children: React.ReactNode;
}

/**
 * ThemeProvider - Wraps application content with theme support
 *
 * Responsibilities:
 * 1. Applies awsui-visual-refresh class for all design systems
 * 2. Manages dark mode based on debug settings
 * 3. Themes public Cloudscape primary buttons to match internal Cloudscape (amber)
 *
 * Supports all design systems in the application:
 * - Public Cloudscape (@cloudscape-design/components)
 * - Internal Cloudscape/Polaris (@amzn/awsui-components-react/polaris/*)
 * - AWS-UI Global Styles (@amzn/awsui-global-styles)
 *
 * Applies mode to both Cloudscape and AWS-UI to ensure consistent theming.
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { settings } = useDebug();

  // Carries light and dark values, so it is applied once and follows applyMode
  React.useEffect(() => applyTheme({ theme: primaryButtonTheme }).reset, []);

  // Apply dark mode based on debug settings to both design systems
  React.useEffect(() => {
    if (settings.darkMode) {
      // When dark mode is enabled, respect system preference
      if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
        applyCloudscapeMode(CloudscapeMode.Dark);
        applyAwsuiMode(AwsuiMode.Dark);
      } else {
        applyCloudscapeMode(CloudscapeMode.Light);
        applyAwsuiMode(AwsuiMode.Light);
      }
    } else {
      // When dark mode is disabled, always use light mode
      applyCloudscapeMode(CloudscapeMode.Light);
      applyAwsuiMode(AwsuiMode.Light);
    }
  }, [settings.darkMode]);

  return <div>{children}</div>;
};
